import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";

const passwordStrengthHint = (pw) => {
  if (!pw) return null;
  if (pw.length < 8) return { ok: false, msg: "Add at least 8 characters." };
  if (!/[A-Za-z]/.test(pw)) return { ok: false, msg: "Include a letter." };
  if (!/\d/.test(pw)) return { ok: false, msg: "Include a number." };
  return { ok: true, msg: "Looks good." };
};

/**
 * Consumes the one-time reset token from `?token=...`. On success, redirects
 * to /login with a success toast state.
 */
export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const strength = useMemo(() => passwordStrengthHint(password), [password]);
  const canSubmit = token && strength?.ok && password === confirm && !submitting;

  useEffect(() => {
    if (done) {
      const t = setTimeout(() => navigate("/login", { replace: true }), 3500);
      return () => clearTimeout(t);
    }
  }, [done, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Could not reset your password. Please request a new link.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-brand-cream/40 flex flex-col">
        <Header />
        <main className="flex-1 pt-[88px] sm:pt-[100px] pb-16">
          <div className="max-w-md mx-auto px-5 sm:px-6">
            <div
              data-testid="reset-password-no-token"
              className="rounded-3xl bg-white border border-black/5 shadow-sm p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <h1 className="font-display font-black text-2xl text-brand-charcoal">
                  Link missing
                </h1>
              </div>
              <p className="text-sm text-brand-charcoal/70 mb-5">
                This page needs a reset token in the URL. Please use the link
                from the password reset email, or request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2.5 text-xs font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
              >
                Request a new link
              </Link>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-cream/40 flex flex-col">
      <Header />
      <main className="flex-1 pt-[88px] sm:pt-[100px] pb-16">
        <div className="max-w-md mx-auto px-5 sm:px-6">
          <Link
            to="/login"
            data-testid="reset-back-to-login"
            className="inline-flex items-center gap-1.5 text-sm text-brand-charcoal/60 hover:text-brand-teal transition-colors mb-6"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to sign in
          </Link>

          <div className="rounded-3xl bg-white border border-black/5 shadow-sm p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-5">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-teal/10 text-brand-teal">
                <Lock className="h-4 w-4" />
              </span>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-teal">
                  Choose a new password
                </p>
                <h1
                  data-testid="reset-password-title"
                  className="font-display font-black text-2xl sm:text-3xl text-brand-charcoal leading-tight"
                >
                  Reset your password
                </h1>
              </div>
            </div>

            {done ? (
              <div
                data-testid="reset-password-success"
                className="rounded-2xl bg-brand-green/10 border border-brand-green/30 p-5"
              >
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-brand-green shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-brand-charcoal text-[15px]">
                      Password updated.
                    </p>
                    <p className="text-sm text-brand-charcoal/70 mt-1.5">
                      You're all set. Taking you to sign in…
                    </p>
                    <Link
                      to="/login"
                      className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-brand-charcoal text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
                    >
                      Sign in now
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                    New password *
                  </Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="reset-password-input"
                    placeholder="At least 8 characters, with a letter and a number"
                    className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                  {strength && (
                    <p
                      data-testid="reset-password-strength"
                      className={`text-[11.5px] ${
                        strength.ok ? "text-brand-green" : "text-brand-charcoal/60"
                      }`}
                    >
                      {strength.msg}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/65">
                    Confirm new password *
                  </Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    data-testid="reset-password-confirm"
                    className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                  {confirm && password !== confirm && (
                    <p className="text-[11.5px] text-red-600">
                      Passwords don't match yet.
                    </p>
                  )}
                </div>

                {error && (
                  <p
                    data-testid="reset-password-error"
                    role="alert"
                    className="text-[12.5px] text-red-600"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!canSubmit}
                  data-testid="reset-password-submit"
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] hover:bg-brand-teal transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      Updating…
                    </>
                  ) : (
                    <>
                      <Lock className="h-3.5 w-3.5" />
                      Update password
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
}
